import React, { useState, useEffect } from 'react';
import { Plus } from 'lucide-react';
import { qrAPI, adminQRAPI } from '../../utils/api';
import { toast } from '../../hooks/use-toast';
import { Toaster } from '../ui/toaster';

const QR_OPTIONS = [1, 2, 5, 10, 15, 30, 60];

function Section({ title, children }) {
  return (
    <div style={{ background: '#fff', border: '1px solid rgba(0,0,0,0.06)', borderRadius: 16, padding: 20 }}>
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#A1A1A6', marginBottom: 14 }}>
        {title}
      </div>
      {children}
    </div>
  );
}

function SettingRow({ icon, title, sub, badge, badgeColor }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '14px 16px', background: '#F5F5F7', borderRadius: 12, marginBottom: 10,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{
          width: 36, height: 36, borderRadius: 8,
          background: badgeColor || '#0A0A0A', color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontWeight: 800, fontStyle: 'italic', fontSize: 14,
        }}>
          {icon}
        </div>
        <div>
          <div style={{ fontWeight: 600, fontSize: 13 }}>{title}</div>
          <div style={{ fontSize: 11, color: '#6B6B70' }}>{sub}</div>
        </div>
      </div>
      {badge && (
        <span style={{
          display: 'inline-flex', alignItems: 'center',
          padding: '3px 10px', borderRadius: 9999,
          fontSize: 11, fontWeight: 600,
          background: 'oklch(0.92 0.10 250)', color: '#091830',
        }}>
          {badge}
        </span>
      )}
    </div>
  );
}

const TEAM = [
  { n: 'Operación Outlet', e: 'admin@miembros.mx', r: 'Owner' },
  { n: 'Yair Ortiz',       e: 'yair@miembros.mx',  r: 'Editor' },
  { n: 'Lucía A. (caja)', e: 'lucia@miembros.mx', r: 'Tienda' },
];

const AdminSettingsPage = () => {
  const [qrDuration, setQrDuration] = useState(15);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    qrAPI.getSettings()
      .then(res => setQrDuration(res.data?.duration ?? 15))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleSaveQR = async () => {
    try {
      setSaving(true);
      await adminQRAPI.updateSettings(qrDuration);
      toast({ title: 'Configuración guardada', description: `QR durará ${qrDuration} minutos.` });
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <Toaster />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>

        {/* Pasarela de pagos */}
        <Section title="Pasarela de pagos">
          <SettingRow
            icon="S"
            badgeColor="#635BFF"
            title="Stripe · MX"
            sub="conectada · acct_1OutletMx00"
            badge="activa"
          />
          <SettingRow
            icon="A"
            badgeColor="#000"
            title="Apple Pay · Domain"
            sub="verificada"
            badge="verificada"
          />
          <div style={{ marginTop: 14, fontSize: 12, color: '#6B6B70', lineHeight: 1.6 }}>
            Precios actuales:{' '}
            <strong style={{ color: '#0A0A0A', fontFamily: 'monospace' }}>$650.00 MXN / año</strong> y{' '}
            <strong style={{ color: '#0A0A0A', fontFamily: 'monospace' }}>$277.00 MXN / mes</strong>.
            El cambio aplica a nuevos miembros — los existentes mantienen su tarifa hasta el siguiente corte.
          </div>
        </Section>

        {/* Equipo */}
        <Section title="Equipo">
          {TEAM.map((m, i) => (
            <div
              key={i}
              style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: '12px 0',
                borderBottom: i < TEAM.length - 1 ? '1px solid rgba(0,0,0,0.05)' : 'none',
              }}
            >
              <div style={{
                width: 32, height: 32, borderRadius: '50%', background: '#0A0A0A', color: '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 11, fontWeight: 700, flexShrink: 0,
              }}>
                {m.n.split(' ').map(x => x[0]).slice(0, 2).join('')}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{m.n}</div>
                <div style={{ fontSize: 11, color: '#A1A1A6' }}>{m.e}</div>
              </div>
              <span style={{
                padding: '3px 10px', borderRadius: 9999, fontSize: 11, fontWeight: 600,
                background: '#F5F5F7', color: '#0A0A0A',
              }}>
                {m.r}
              </span>
            </div>
          ))}
          <button style={{
            marginTop: 12, height: 36, padding: '0 12px', borderRadius: 9,
            background: '#0A0A0A', color: '#fff', fontSize: 12, fontWeight: 600,
            border: 'none', cursor: 'pointer',
            display: 'inline-flex', alignItems: 'center', gap: 6,
          }}>
            <Plus size={12} /> Invitar miembro
          </button>
        </Section>

        {/* QR Settings */}
        <Section title="Configuración de QR dinámico">
          {loading ? (
            <div style={{ color: '#A1A1A6', fontSize: 13 }}>Cargando…</div>
          ) : (
            <>
              <div style={{ fontSize: 12, color: '#6B6B70', marginBottom: 10, lineHeight: 1.5 }}>
                Define cuántos minutos dura el código QR antes de expirar. 15 minutos es el balance ideal entre seguridad y comodidad.
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
                {QR_OPTIONS.map(opt => (
                  <button
                    key={opt}
                    onClick={() => setQrDuration(opt)}
                    style={{
                      height: 34, padding: '0 14px', borderRadius: 9,
                      background: qrDuration === opt ? '#0A0A0A' : '#F5F5F7',
                      color: qrDuration === opt ? '#fff' : '#0A0A0A',
                      fontSize: 12.5, fontWeight: 600, border: 'none', cursor: 'pointer',
                    }}
                  >
                    {opt === 60 ? '1 hora' : `${opt} min`}
                  </button>
                ))}
              </div>
              <button
                onClick={handleSaveQR}
                disabled={saving}
                style={{
                  height: 38, padding: '0 18px', borderRadius: 9,
                  background: '#0A0A0A', color: '#fff',
                  fontSize: 13, fontWeight: 600, border: 'none',
                  cursor: saving ? 'not-allowed' : 'pointer',
                }}
              >
                {saving ? 'Guardando…' : 'Guardar configuración QR'}
              </button>
            </>
          )}
        </Section>

        {/* App info */}
        <Section title="Información de la app">
          {[
            ['Versión', '1.4.2'],
            ['Entorno', 'Production'],
            ['Outlet', 'Outlet Puebla'],
            ['Suscripción', '$650 MXN / año · $277 MXN / mes'],
          ].map(([label, val]) => (
            <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid rgba(0,0,0,0.04)' }}>
              <span style={{ fontSize: 13, color: '#6B6B70' }}>{label}</span>
              <span style={{ fontSize: 13, fontWeight: 600, fontFamily: val.startsWith('$') || val.match(/^\d/) ? 'monospace' : undefined }}>{val}</span>
            </div>
          ))}
        </Section>

      </div>
    </div>
  );
};

export default AdminSettingsPage;
